import PropTypes from 'prop-types';
import React, { useCallback, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';
import { timer } from 'rxjs';
import { delay, filter, map, takeWhile } from 'rxjs/operators';

import Heading from 'app/components/shared/Heading';
import SyndeticsUnboundEnrichment from 'app/components/bibs/shared/SyndeticsUnboundEnrichment';
import VendorAttributionLabel from 'app/components/bibs/shared/VendorAttributionLabel';
import { getRecommendationProviderDisplayName, getUpcFromCatalogBib } from 'app/helpers/bib/bibHelpers';
import useSyndeticsUnbound, { SUPPORTED_ENRICHMENT_IDS } from 'app/hooks/useSyndeticsUnbound';
import { selectBibEntities, selectCatalogBibEntities } from 'app/selectors/EntitiesSelector';

import './SyndeticsUnboundSameSeries.scss';

const SyndeticsUnboundSameSeriesContainer = ({ metadataId }) => {
  const bibs = useSelector(selectBibEntities);
  const catalogBibs = useSelector(selectCatalogBibEntities);
  const [hasSeriesEntries, setHasSeriesEntries] = useState(false);

  const isbn = bibs.getIn([metadataId, 'briefInfo', 'isbns', 0]);
  const upc = getUpcFromCatalogBib(catalogBibs.get(metadataId));

  const onSyndeticsUnboundLoaded = useCallback(
    connectorApi => {
      const { isEnrichmentShown, populate } = connectorApi;

      populate({ isbn, upc });

      // There is a race-condition between when the page renders
      // and when Syndetics Unbound is able to populate the 'series' enrichment.
      // This code effectively polls the UI every NUM_INTERVAL_MILLISECONDS milliseconds
      // up to a maxiumum of NUM_MAX_TRIES tries
      // to see if Syndetics Unbound has `series` enrichment data.
      // When there is 'series' data, the containing elements gets unhidden.
      const NUM_MAX_TRIES = 5;
      const NUM_INTERVAL_MILLISECONDS = 500;
      timer(0, NUM_INTERVAL_MILLISECONDS)
        .pipe(
          delay(NUM_INTERVAL_MILLISECONDS),
          takeWhile(i => i < NUM_MAX_TRIES),
          map(() => isEnrichmentShown(SUPPORTED_ENRICHMENT_IDS.SERIES)),
          takeWhile(isShown => !isShown, true),
          filter(isShown => isShown)
        )
        .subscribe(isShown => {
          setHasSeriesEntries(isShown);
        });
    },
    [isbn, setHasSeriesEntries, upc]
  );

  useSyndeticsUnbound(onSyndeticsUnboundLoaded);

  return (
    <div className="cp-syndetics-unbound-same-series" style={{ display: hasSeriesEntries ? 'block' : 'none' }}>
      <div className="top">
        <Heading tagName="h3" size="medium" variant>
          <FormattedMessage id="in_the_same_series" />
        </Heading>
        <VendorAttributionLabel name={getRecommendationProviderDisplayName('SYNDETICS_UNBOUND')} />
      </div>
      <SyndeticsUnboundEnrichment enrichmentId={SUPPORTED_ENRICHMENT_IDS.SERIES} hasTitle={false} />
    </div>
  );
};

SyndeticsUnboundSameSeriesContainer.propTypes = {
  metadataId: PropTypes.string.isRequired
};

export default SyndeticsUnboundSameSeriesContainer;
